/* eslint-disable jsx-a11y/anchor-has-content, jsx-a11y/control-has-associated-label */
/*
 * Copyright 2020, IntraLinks, Inc. All rights reserved.
 */
import React from 'react';
import { Trans, WithTranslation, withTranslation } from 'react-i18next';
import {
  IlButton, IlIcon, IlIconInitializer, IlModalV2
} from 'il-framework-component';
import { connect } from 'react-redux';
import classNames from 'classnames';
import queryString from 'query-string';
import _ from 'lodash';
import {
  EULA_ACTIONS, EULA_HREF, EULA_ITEMS, I18nNamespaces
} from '../../app/shared/utils/constants.util';
import Styles from './Eula.scss';
import Wrapper from '../../app/shared/components/Wrapper/Wrapper';
import { AppDispatch, RootState } from '../../app/store';
import eulaAccepted from './EulaSlice';
import { IProfile, IProfileProperties } from '../../domain/models/IProfile';
import { getProfileForEulaCheck, getProfileForProfileSetup, profileSetup } from '../Profile/profileSlice';
import ProfileSetupComponent from './ProfileSetup';

interface EulaProps extends WithTranslation, IDispatchProps, IStateProps {
}

interface IState {
  hideConfirmModal: boolean;
  btnDisabled: boolean;
  redirectUrl?: string;
  eulaStatusUpdated?: boolean;
  eulaCollapsed?: boolean;
  newEulaVersion?: string;
}

class Eula extends React.Component<EulaProps, IState> {
  constructor(props: EulaProps) {
    super(props);
    this.state = {
      hideConfirmModal: true,
      btnDisabled: false,
      redirectUrl: undefined,
      eulaStatusUpdated: false,
      eulaCollapsed: false,
      newEulaVersion: ''
    };
  }

  async componentDidMount(): Promise<void> {
    const { dispatchGetProfile } = this.props;
    const eulaVersion = queryString.parse(window.location.search);
    this.setState({ newEulaVersion: eulaVersion.version as string });
    await dispatchGetProfile(['name', 'preferences', 'eulaDeclined', 'eulaVersion']);
  }

  onDeclineClicked = () => {
    this.setState({ hideConfirmModal: false });
  };

  onCloseClicked = () => {
    this.setState({ hideConfirmModal: true });
  };

  async onAccept(action: string) {
    const { dispatchEulaAccepted, dispatchGetProfileProfileInfo } = this.props;
    this.setState({ btnDisabled: true });
    let redirectUrl;
    // eslint-disable-next-line no-useless-catch
    try {
      const response: any = await dispatchEulaAccepted(action);
      redirectUrl = response && response.data && response.data.data ? response.data.data.redirecturl : response.request.responseURL;
      if (action === EULA_ACTIONS.ACCEPT) {
        await dispatchGetProfileProfileInfo(['name', 'preferences']);
        this.setState({ redirectUrl, eulaStatusUpdated: true, eulaCollapsed: true });
      } else {
        window.location.href = redirectUrl;
      }
    } catch (err) {
      window.location.href = redirectUrl;
      throw err;
    }
  }

  async onProfileSetup(profileInfo: Partial<IProfileProperties>) {
    const { dispatchUpdateProfileSetup } = this.props;
    const { redirectUrl } = this.state;
    try {
      await dispatchUpdateProfileSetup(profileInfo);
      window.location.href = redirectUrl as string;
    } catch (err) {
      window.location.href = redirectUrl as string;
    }
  };

  renderModal = () => {
    const { t } = this.props;
    return (
      <div className={Styles['eula-confirm-modal']}>
        <form>
          <IlModalV2
            dialogBody={t('eula.declineText', { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}
            size='xs'
            modalHeading={t('eula.confirmTitle', { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}
            passiveModal
            open
            onRequestClose={() => this.onCloseClicked()}
            preventCloseOnClickOutside
            selectorPrimaryFocus='#eulaGoBackBtn'
            primaryButtonText={t('eula.decline', { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}
            primaryButtonId='eulaDeclineBtn'
            secondaryButtonText={t('eula.goBack', { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}
            secondaryButtonId='eulaGoBackBtn'
            tertiaryButtonText=''
            primaryButtonClick={() => this.onAccept(EULA_ACTIONS.DECLINE)}
            secondaryButtonClick={() => this.onCloseClicked()}
          />
        </form>
      </div>
    );
  };

  onTranslation = (translationKey: string, linkAddress:string) => (
    <Trans
      i18nKey={translationKey}
      components={{
        a: <a href={linkAddress} target='_blank' rel='noopener noreferrer' />
      }}
    />
  );

  renderSingleContent = (item: string) => {
    const content = [];
    const { t } = this.props;
    for (let i = 0; i < EULA_ITEMS[item]; i += 1) {
      const eulaTile = `${item}${i + 1}`;
      if (EULA_HREF[eulaTile]) {
        const data = t(`eulaContent.${eulaTile}`, { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] });
        const key = Object.keys(EULA_HREF[`${eulaTile}`])[0];
        content.push(
          <p className={Styles['il-eula-content-sub-content']}>
            {this.onTranslation(data, EULA_HREF[eulaTile][key])}
          </p>
        );
      } else {
        content.push(
          <p className={Styles['il-eula-content-sub-content']}>
            {t(`eulaContent.${item}${i + 1}`, { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}
          </p>
        );
      }
    }
    return content;
  };

  renderContent = () => {
    const { t } = this.props;
    return (
      <div>
        <p className={Styles['il-eula-content-tiles']}>
          <strong>{t('eula.endUserAgreement', { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}</strong>
        </p>
        <p className={Styles['il-eula-content-sub-content']}>
          {t('eula.content', { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}
        </p>
        {Object.keys(EULA_ITEMS).map((item, val) => (
          <>
            <p className={Styles['il-eula-content-tiles']}>
              <strong>{val + 1}. {t(`eulaTitle.${item}`, { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}</strong>
            </p>
            {this.renderSingleContent(item)}
          </>
        ))}
      </div>
    );
  };

  renderSetupProfile = () => {
    const { profileSetupDetails, profileSetupDetails: { eulaDeclined, eulaVersion } = {} } = this.props;
    const { eulaStatusUpdated } = this.state;
    return (
      <ProfileSetupComponent
        profileDetails={profileSetupDetails}
        onSave={(profileInfo) => this.onProfileSetup(profileInfo)}
        eulaActivated={(!eulaDeclined && eulaVersion === 1) || eulaStatusUpdated}
      />
    );
  };

  render() {
    const { t, profileSetupDetails: { eulaDeclined, eulaVersion } = {} } = this.props;
    const {
      hideConfirmModal, btnDisabled, eulaCollapsed, newEulaVersion
    } = this.state;
    return (
      <div>
        <div>
          <IlIconInitializer />

          <Wrapper>
            <div className={classNames(Styles['eula-body'], { [Styles['eula-body-collapsed']]: eulaCollapsed })}>
              <div>
                <div className={Styles['eula-header-container']}>
                  <div className={Styles['eula-header']}>
                    {t('eula.title', { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}
                  </div>
                  <IlButton
                    className={Styles.collapseButton}
                    kind='ghost'
                    role='button'
                    onClick={(): void => { this.setState({ eulaCollapsed: !eulaCollapsed }); }}
                    type='button'
                  >
                    <IlIcon name={eulaCollapsed ? 'chevron-down' : 'chevron-up'} className={Styles.collapseIcon} />
                  </IlButton>
                </div>
                {!eulaCollapsed
                && (
                <div>
                  <div className={Styles['eula-before-block']}>
                    {t('eula.header', { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}
                  </div>
                  <div className={Styles['eula-term-info']}>
                    {!_.isEmpty(newEulaVersion) && (
                      <p>{t('eula.eulaUpdateInfo', { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}</p>
                    )}
                    <a href='https://www.intralinks.com/eula'>
                      {t('eula.eulaLink', { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}
                    </a>
                  </div>
                  <div className={Styles['eula-agreement-wrapper']}>
                    <div className={Styles['eula-agreement-body']}>
                      {this.renderContent()}
                    </div>
                  </div>
                  {(!eulaDeclined && eulaVersion === 1) ? (
                    <div className={Styles['il-btn-container']}>
                      <div className={Styles.acceptBtn}>
                        <IlButton
                          className={Styles.btn}
                          size='small'
                          kind='primary'
                          onClick={() => this.onAccept(EULA_ACTIONS.ACCEPT)}
                          disabled
                        >{t('eula.accepted', { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}
                        </IlButton>
                      </div>
                    </div>
                  )
                    : (
                      <div className={Styles['il-btn-container']}>
                        <div className={Styles.declineBtn}>
                          <IlButton
                            className={Styles.btn}
                            size='small'
                            kind='primary'
                            onClick={() => this.onDeclineClicked()}
                            disabled={btnDisabled}
                          >{t('eula.decline', { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}
                          </IlButton>
                        </div>
                        <div className={Styles.acceptBtn}>
                          <IlButton
                            className={Styles.btn}
                            size='small'
                            kind='primary'
                            onClick={() => this.onAccept(EULA_ACTIONS.ACCEPT)}
                            disabled={btnDisabled}
                          >{t('eula.accept', { ns: [I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT] })}
                          </IlButton>
                        </div>
                      </div>
                    )}
                </div>
                )}
              </div>
            </div>
            {this.renderSetupProfile()}
          </Wrapper>
        </div>
        {!hideConfirmModal && this.renderModal()}

      </div>
    );
  }
}
interface IStateProps {
  profileSetupDetails?: Partial<IProfileProperties>;
}
interface IDispatchProps {
  dispatchEulaAccepted: (action: string) => Promise<void>;
  dispatchGetProfile: (fields?: (keyof IProfile)[] | undefined) => void;
  dispatchGetProfileProfileInfo: (fields?: (keyof IProfile)[] | undefined) => void;
  dispatchUpdateProfileSetup: (req: any) => void;

}
const mapStateToProps = (state: RootState): IStateProps => {
  const { profile: { profileSetupDetails } } = state;
  return {
    profileSetupDetails
  };
};

const mapDispatchToProps = (dispatch: AppDispatch): IDispatchProps => ({
  dispatchEulaAccepted: (action): void => dispatch(
    eulaAccepted(action)
  ),
  dispatchGetProfile: (fields): void => dispatch(getProfileForEulaCheck(fields)),
  dispatchGetProfileProfileInfo: (fields): void => dispatch(getProfileForProfileSetup(fields)),
  dispatchUpdateProfileSetup: (req: any): void => dispatch(profileSetup(req))

}) as IDispatchProps;

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation([I18nNamespaces.PROFILE, I18nNamespaces.DEFAULT])(Eula));
